import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import { useEffect, useState } from "react";
import {
  TextB, TextItalic, TextUnderline, ListBullets, ListNumbers,
  Link as LinkIcon, Quotes, ArrowCounterClockwise, ArrowClockwise, Code, TextH,
} from "@phosphor-icons/react";

/**
 * Rich text editor for the welcome email body.
 *
 * `value` / `onChange` speak HTML. The template tokens (e.g. `{{first_name}}`) are
 * passed through verbatim by the browser and rendered as plain text — clicking a
 * "Insert token" chip pushes the raw token string into the editor at the caret.
 */
export default function RichTextEditor({ value, onChange, placeholder }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: "rte-content prose max-w-none min-h-[220px] p-4 focus:outline-none text-sm text-[var(--gc-navy)]",
        "data-placeholder": placeholder || "Write the email body…",
      },
    },
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
  });

  // Keep external value in sync (e.g. Reset to default)
  useEffect(() => {
    if (!editor) return;
    const cur = editor.getHTML();
    if ((value || "") !== cur) {
      editor.commands.setContent(value || "", { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return <div className="border border-[var(--gc-border)] min-h-[220px] p-4 text-[var(--gc-text-muted)] text-sm">Loading editor…</div>;

  return (
    <div className="border border-[var(--gc-border)] focus-within:border-[var(--gc-navy)] transition-colors" data-testid="rte-root">
      <Toolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
}

function Toolbar({ editor }) {
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");

  const isActive = (name, opts) => editor.isActive(name, opts);
  const btn = (active) =>
    `p-2 hover:bg-[var(--gc-bg-alt)] transition-colors ${active ? "bg-[var(--gc-navy)] text-white hover:bg-[var(--gc-navy)]" : "text-[var(--gc-navy)]"}`;

  const openLink = () => {
    const prev = editor.getAttributes("link")?.href || "";
    setLinkUrl(prev);
    setLinkOpen(true);
  };
  const applyLink = () => {
    if (!linkUrl) {
      editor.chain().focus().unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange("link").setLink({ href: linkUrl }).run();
    }
    setLinkOpen(false);
    setLinkUrl("");
  };

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-[var(--gc-border)] bg-[var(--gc-bg-alt)] p-1 text-sm relative">
      <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={btn(isActive("bold"))} title="Bold (⌘B)" data-testid="rte-bold">
        <TextB size={16} weight="bold" />
      </button>
      <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={btn(isActive("italic"))} title="Italic (⌘I)" data-testid="rte-italic">
        <TextItalic size={16} weight="bold" />
      </button>
      <button type="button" onClick={() => editor.chain().focus().toggleUnderline().run()} className={btn(isActive("underline"))} title="Underline (⌘U)" data-testid="rte-underline">
        <TextUnderline size={16} weight="bold" />
      </button>
      <div className="w-px h-5 bg-[var(--gc-border)] mx-1" />
      <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={btn(isActive("heading", { level: 2 }))} title="Heading" data-testid="rte-h2">
        <TextH size={16} weight="bold" />
      </button>
      <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={btn(isActive("bulletList"))} title="Bulleted list" data-testid="rte-ul">
        <ListBullets size={16} weight="bold" />
      </button>
      <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={btn(isActive("orderedList"))} title="Numbered list" data-testid="rte-ol">
        <ListNumbers size={16} weight="bold" />
      </button>
      <button type="button" onClick={() => editor.chain().focus().toggleBlockquote().run()} className={btn(isActive("blockquote"))} title="Blockquote" data-testid="rte-quote">
        <Quotes size={16} weight="bold" />
      </button>
      <div className="w-px h-5 bg-[var(--gc-border)] mx-1" />
      <button type="button" onClick={openLink} className={btn(isActive("link"))} title="Add / edit link" data-testid="rte-link">
        <LinkIcon size={16} weight="bold" />
      </button>
      <button type="button" onClick={() => editor.chain().focus().toggleCode().run()} className={btn(isActive("code"))} title="Inline code" data-testid="rte-code">
        <Code size={16} weight="bold" />
      </button>
      <div className="w-px h-5 bg-[var(--gc-border)] mx-1" />
      <button type="button" onClick={() => editor.chain().focus().undo().run()} className={btn(false)} title="Undo (⌘Z)" data-testid="rte-undo">
        <ArrowCounterClockwise size={16} weight="bold" />
      </button>
      <button type="button" onClick={() => editor.chain().focus().redo().run()} className={btn(false)} title="Redo (⌘⇧Z)" data-testid="rte-redo">
        <ArrowClockwise size={16} weight="bold" />
      </button>

      {linkOpen && (
        <div className="absolute left-2 top-full mt-1 z-10 bg-white border border-[var(--gc-navy)] shadow-lg p-2 flex items-center gap-2" data-testid="rte-link-popover">
          <input
            autoFocus
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), applyLink())}
            placeholder="https://…"
            className="gc-input w-64 py-1 text-xs"
            data-testid="rte-link-input"
          />
          <button type="button" onClick={applyLink} className="btn-solid px-3 py-1 text-xs" data-testid="rte-link-apply">Apply</button>
          <button type="button" onClick={() => { setLinkOpen(false); setLinkUrl(""); }} className="text-xs link-underline">Cancel</button>
        </div>
      )}
    </div>
  );
}
